export const MaterialNutritionalParameter = (json: IMaterialNutritional): IMaterialNutritionalModel => {
	const fromJSON = (itemJson: IMaterialNutritional) => {
		const { name, ...rest } = itemJson
		return {
			name: LangContentToModel(name),
			...rest,
		}
	}

	return { ...fromJSON(json) }
}

export const Material = (json: IDietMaterial): IDietMaterialModel => {
	const {
		name,
		is_active,
		internal_name,
		warehouse_name,
		food_category,
		mechanical_processing_coefficient,
		thermal_processing_coefficient,
		created_at,
		updated_at,
		nutritional_parameters,
		allergens,
		dietary_tags,
		...rest
	} = json

	return {
		...rest,
		isActive: is_active,
		name: LangContentToModel(name),
		internalName: internal_name,
		warehouseName: warehouse_name,
		foodCategory: food_category,
		createdAt: created_at ? new Date(created_at) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
		thermalProcessingCoefficient: thermal_processing_coefficient,
		mechanicalProcessingCoefficient: mechanical_processing_coefficient,
		nutritionalParameters: nutritional_parameters
			? nutritional_parameters.map(np => MaterialNutritionalParameter(np))
			: [],
		allergens: allergens ? allergens.map(a => DietAllergen(a)) : [],
		dietaryTags: dietary_tags ? dietary_tags.map(d => DietDietaryTag(d)) : [],
	}
}

export const materialToJSON = (obj: Partial<IMaterialModel>): Partial<IMaterial> => {
	const { isActive, internalName, warehouseName, ...rest } = obj
	return {
		is_active: isActive,
		internal_name: internalName,
		warehouse_name: warehouseName,
		...rest,
	}
}

export const materialNutritionalParameterToJSON = (
	obj: Partial<IMaterialNutritionalModel>,
): Partial<IMaterialNutritional> => {
	const { ...rest } = obj
	return {
		...rest,
	}
}

export const dietRawMaterialToModel = (json: IDietRawMaterial): IDietRawMaterialModel => {
	const { name, internal_name, warehouse_name, is_active, ...rest } = json

	return {
		...rest,
		name,
		namePL: getDefaultTranslation(name) ?? '',
		internalName: internal_name,
		warehouseName: warehouse_name,
		isActive: is_active,
	}
}

export const MaterialCategory = (json: IDietMaterialCategory): IDietMaterialCategoryModel => {
	const { name, ...rest } = json

	return {
		...rest,
		name: LangContentToModel(name),
	}
}

/**
 * History
 */
export const DietMaterialEditHistory = (json: IDietMaterialHistory): IDietMaterialHistoryModel => {
	const { date, ...rest } = json

	return {
		...rest,
		date: date ? new Date(date) : null,
	}
}

/**
 * Material Forms
 */
const DietMaterialBaseToPayload = (
	model: IDietMaterialForm,
	allergens: ISelectOption<number>[],
	dietaryTags: ISelectOption<number>[],
): IDietMaterialBasePayload => {
	const {
		internalName,
		warehouseName,
		foodCategory,
		unit,
		thermalProcessingCoefficient,
		mechanicalProcessingCoefficient,
		ingredients,
		...rest
	} = model

	const array: Array<IDietMaterialNutritionalParameter> = []
	Object.entries(rest).map(([name, _value]) => {
		if (name.startsWith('nutritionalParameter')) {
			const id = Number(name.replace(/\D/g, ''))
			const value = Number.isNaN(Number(_value)) || Number(_value) === 0 ? null : Number(_value)

			if (!value) return undefined

			array.push({ id, value })
		}

		return undefined
	})

	return {
		unit,
		ingredients,
		internal_name: internalName,
		warehouse_name: warehouseName,
		food_category: foodCategory,
		thermal_processing_coefficient: thermalProcessingCoefficient,
		mechanical_processing_coefficient: mechanicalProcessingCoefficient,
		is_active: true,
		allergens: allergens ? allergens.map(allergen => allergen.value) : [],
		dietary_tags: dietaryTags ? dietaryTags.map(tag => tag.value) : [],
		nutritional_values: array,
	}
}

export const DietMaterialToCreatePayload = (
	model: IDietMaterialForm,
	allergens: ISelectOption<number>[],
	dietaryTags: ISelectOption<number>[],
): IDietMaterialCreatePayload => {
	const { name } = model

	const payload = DietMaterialBaseToPayload(model, allergens, dietaryTags)

	return {
		name: generateLangContentArray(name),
		...payload,
	}
}

export const DietMaterialToUpdatePayload = (
	base: IDietMaterialModel,
	model: IDietMaterialForm,
	allergens: ISelectOption<number>[],
	dietaryTags: ISelectOption<number>[],
): IDietMaterialUpdatePayload => {
	const { id, name } = base

	const payload = DietMaterialBaseToPayload(model, allergens, dietaryTags)

	return {
		id,
		name: LangContentToJson(name, model.name),
		...payload,
	}
}
